import React from 'react';
import { ExtendedOvertimeRequest } from '@/types/supabase-types';
import { formatDate, formatHours } from '@/lib/utils';
import { OvertimeStatus } from '@/types/enums';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CheckIcon, XIcon, Clock, CalendarIcon, Building2, User, FileText, ShieldAlert } from 'lucide-react';
import { useOvertime } from '@/contexts/overtime/useOvertime';
import { toast } from 'sonner';
import { StatusBadge } from '@/utils/statusUtils';

interface RequestDetailsModalProps {
  request: ExtendedOvertimeRequest | null;
  isOpen: boolean;
  onClose: () => void;
  onApprove?: (id: string) => void;
  onReject?: (id: string) => void;
}

export function RequestDetailsModal({
  request,
  isOpen,
  onClose,
  onApprove,
  onReject
}: RequestDetailsModalProps) {
  const { approveOvertimeRequest, rejectOvertimeRequest, shifts } = useOvertime();
  const [isLoading, setIsLoading] = React.useState(false);

  /**
   * Formats shift information with detailed timing and break deduction
   * @param shiftId - The shift ID to look up
   * @param requestDate - The request date to find day-specific timing
   * @returns Formatted shift string with name, code, timing, and break info
   */
  const formatShiftDetails = (shiftId: string | undefined | null, requestDate?: string): string => {
    if (!shiftId) return 'No shift assigned';
    if (!shifts?.length) return 'Shift data unavailable';
    
    const shift = shifts.find(s => s.id === shiftId);
    if (!shift) return `Unknown Shift (ID: ${shiftId})`;

    const shiftName = shift.code ? `${shift.name} (${shift.code})` : shift.name;
    
    // Return basic name if no detailed timing available
    if (!shift.day_time_deduction?.length) return shiftName;
    
    const timingBlock = findRelevantTimingBlock(shift.day_time_deduction, requestDate);
    if (!timingBlock) return shiftName;
    
    return buildShiftDisplayString(shiftName, timingBlock);
  };

  /**
   * Finds the appropriate timing block for the given date
   */
  const findRelevantTimingBlock = (dayTimeDeduction: Array<{ day: number | number[]; start_time: string; end_time: string }>, requestDate?: string) => {
    if (!requestDate) return dayTimeDeduction[0];
    
    const requestDay = new Date(requestDate).getDay();
    // Convert JS day (0=Sunday, 1=Monday, ..., 6=Saturday) to our format (1=Sunday, 2=Monday, ..., 7=Saturday)
    const dayFormat = requestDay + 1;
    
    return dayTimeDeduction.find(block => {
      return Array.isArray(block.day) 
        ? block.day.includes(dayFormat)
        : block.day === dayFormat;
    }) || dayTimeDeduction[0];
  };

  /**
   * Builds the formatted shift display string
   */
  const buildShiftDisplayString = (shiftName: string, timingBlock: { start_time?: string; end_time?: string; break_hrs?: number }): string => {
    const startTime = timingBlock.start_time || 'N/A';
    const endTime = timingBlock.end_time || 'N/A';
    const breakHours = timingBlock.break_hrs || 0;
    
    let display = `${shiftName} | ${startTime} - ${endTime}`;
    
    if (breakHours > 0) {
      display += ` | Break Deduction: ${breakHours}h`;
    }
    
    return display;
  };

  const handleApprove = async () => {
    if (!request) return;
    
    try {
      setIsLoading(true);
      await approveOvertimeRequest(request.id, "");
      if (onApprove) onApprove(request.id);
      onClose();
    } catch (error) {
      toast.error('Failed to approve request');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReject = async () => {
    if (!request) return;
    
    try {
      setIsLoading(true);
      await rejectOvertimeRequest(request.id, "");
      if (onReject) onReject(request.id);
      onClose();
    } catch (error) {
      toast.error('Failed to reject request');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };


  if (!request) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Overtime Request Details</DialogTitle>
          <DialogDescription>
            Review the overtime request details below
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <div className="col-span-1 flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <User className="h-4 w-4" /> Employee
            </div>
            <div className="col-span-3 font-medium">{request.employee_name}</div>
          </div>

          <div className="grid grid-cols-4 items-center gap-4">
            <div className="col-span-1 flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Building2 className="h-4 w-4" /> Department
            </div>
            <div className="col-span-3">{request.department}</div>
          </div>

          <div className="grid grid-cols-4 items-center gap-4">
            <div className="col-span-1 flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <CalendarIcon className="h-4 w-4" /> Date
            </div>
            <div className="col-span-3">{formatDate(request.date)}</div>
          </div>

          <div className="grid grid-cols-4 items-center gap-4">
            <div className="col-span-1 flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Clock className="h-4 w-4" /> Hours
            </div>
            <div className="col-span-3">{formatHours(request.hours)}</div>
          </div>

          <div className="grid grid-cols-4 items-center gap-4">
            <div className="col-span-1 flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <FileText className="h-4 w-4" /> Justification
            </div>
            <div className="col-span-3">
              <p className="text-sm">{request.justification_text || request.justification}</p>
            </div>
          </div>

          {/* NEW: Shift Information Display */}
          {(request.shift_assignment_id || request.shift_id) && (
            <div className="grid grid-cols-4 items-center gap-4">
              <div className="col-span-1 flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <Building2 className="h-4 w-4" /> Shift
              </div>
              <div className="col-span-3">
                <p className="text-sm">
                  {formatShiftDetails(
                    request.shift_assignment_id || request.shift_id, 
                    request.date
                  )}
                </p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-4 items-center gap-4">
            <div className="col-span-1 flex items-center gap-2 text-sm font-medium text-muted-foreground">
              Status
            </div>
            <div className="col-span-3">
              <StatusBadge status={request.status} />
            </div>
          </div>

          {(request.vp_approval_required || request.status === OvertimeStatus.PENDING_VP_APPROVAL) && (
            <div className="bg-amber-50 p-3 rounded-md border border-amber-200 flex items-start gap-2 mt-2">
              <ShieldAlert className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">VP Approval Required</p>
                <p className="text-xs text-amber-700 mt-1">
                  This request requires VP approval as it exceeds the monthly overtime threshold.
                </p>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex justify-between sm:justify-between">
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          <div className="flex gap-2">
            {(request.status === OvertimeStatus.PENDING || request.status === OvertimeStatus.PENDING_VP_APPROVAL) && (
              <>
                <Button 
                  variant="destructive" 
                  onClick={handleReject} 
                  disabled={isLoading}
                  className="flex items-center gap-1"
                >
                  <XIcon className="h-4 w-4" />
                  Reject
                </Button>
                <Button 
                  variant="secondary" 
                  onClick={handleApprove} 
                  disabled={isLoading}
                  className="flex items-center gap-1"
                >
                  <CheckIcon className="h-4 w-4" />
                  Approve
                </Button>
              </>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default RequestDetailsModal;
